import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { BlogPost } from '../types'

const BlogOverviewPage: React.FC = () => {
  const [posts, setPosts] = useState<BlogPost[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [categories, setCategories] = useState<string[]>([])
  const [selectedCategory, setSelectedCategory] = useState<string>('')

  useEffect(() => {
    fetchBlogPosts()
    fetchCategories()
  }, [selectedCategory])

  const fetchBlogPosts = async () => {
    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:5000/api'
      const categoryParam = selectedCategory ? `?category=${selectedCategory}` : ''
      const response = await fetch(`${apiUrl}/blog${categoryParam}`)
      const data = await response.json()

      if (!data.success) {
        setError('Fehler beim Laden der Blogbeiträge')
        return
      }

      setPosts(data.data)
    } catch (err) {
      console.error('Error fetching blog posts:', err)
      setError('Fehler beim Laden der Blogbeiträge')
    } finally {
      setLoading(false)
    }
  }

  const fetchCategories = async () => {
    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:5000/api'
      const response = await fetch(`${apiUrl}/blog/categories`)
      const data = await response.json()

      if (data.success) {
        setCategories(data.data)
      }
    } catch (err) {
      console.error('Error fetching categories:', err)
    }
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('de-DE', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-indigo-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Lade Blogbeiträge...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4 text-gray-900">Fehler</h2>
          <p className="text-gray-600 mb-8">{error}</p>
        </div>
      </div>
    )
  }

  return (
    <>
      {/* SEO Meta Tags */}
      <Helmet>
        <title>Blog | PhoneMedic</title>
        <meta name="description" content="Tipps, Anleitungen und News rund um Smartphone-, Tablet- und Laptop-Reparaturen. Expertenwissen vom PhoneMedic Team." />

        {/* Open Graph */}
        <meta property="og:title" content="Blog | PhoneMedic" />
        <meta property="og:description" content="Tipps, Anleitungen und News rund um Smartphone-, Tablet- und Laptop-Reparaturen." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://phone-medic.de/blog" />

        {/* Canonical URL */}
        <link rel="canonical" href="https://phone-medic.de/blog" />
      </Helmet>

      <div className="min-h-screen bg-gray-50 py-8 md:py-12">
        <div className="container mx-auto px-4 max-w-7xl">

          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Blog
            </h1>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Tipps, Anleitungen und News rund um Smartphone-, Tablet- und Laptop-Reparaturen
            </p>
          </div>

          {/* Category Filter */}
          {categories.length > 0 && (
            <div className="mb-8 flex flex-wrap gap-3 justify-center">
              <button
                onClick={() => setSelectedCategory('')}
                className={`px-6 py-2 rounded-full font-medium transition ${
                  selectedCategory === ''
                    ? 'bg-indigo-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-100'
                }`}
              >
                Alle
              </button>
              {categories.map(category => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-6 py-2 rounded-full font-medium transition ${
                    selectedCategory === category
                      ? 'bg-indigo-600 text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>
          )}

          {/* Blog Posts Grid */}
          {posts.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {posts.map((post) => (
                <Link
                  key={post._id}
                  to={`/blog/${post.slug}`}
                  className="bg-white rounded-lg shadow-md hover:shadow-xl transition-all overflow-hidden group"
                >
                  {/* Featured Image */}
                  {post.featuredImage && (
                    <div className="w-full h-48 overflow-hidden">
                      <img
                        src={post.featuredImage}
                        alt={post.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                  )}

                  <div className="p-6">
                    {/* Category & Date */}
                    <div className="flex items-center justify-between mb-3 text-sm">
                      <span className="bg-indigo-100 text-indigo-600 px-3 py-1 rounded-full font-medium">
                        {post.category}
                      </span>
                      <span className="text-gray-500">
                        {formatDate(post.publishedAt || post.createdAt)}
                      </span>
                    </div>

                    {/* Title */}
                    <h2 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-indigo-600 transition">
                      {post.title}
                    </h2>

                    {/* Excerpt */}
                    <p className="text-gray-600 mb-4 line-clamp-3">
                      {post.excerpt}
                    </p>

                    {/* Read More */}
                    <div className="flex items-center text-indigo-600 font-semibold group-hover:underline">
                      Weiterlesen
                      <svg className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-gray-600 text-lg">
                {selectedCategory
                  ? `Keine Beiträge in der Kategorie "${selectedCategory}" gefunden.`
                  : 'Noch keine Blogbeiträge verfügbar.'}
              </p>
            </div>
          )}

        </div>
      </div>
    </>
  )
}

export default BlogOverviewPage
